var _excluded = ["anchorEl", "children", "fixed", "flip", "id", "keepMounted", "modifiers", "onChangePlacement", "open", "placement", "portal", "portalContainer", "role", "style", "transition"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import { createPopper } from '@popperjs/core';
import mergeRefs from 'react-merge-refs';
import Portal from '../portal';
import setRef from '../../utils/set_ref';
import useDirection from '../../direction/use_direction';
import propTypes from './prop_types';
import { jsx as ___EmotionJSX } from "@emotion/core";
/**
 * Translate our popover placement to popover.js placement
 */
export function translatePlacement(placement, isRTL) {
  var [first, last] = placement.split('-');
  var horizontalPlacements = ['leading', 'left', 'right', 'trailing'];
  var isHorizontal = horizontalPlacements.includes(first);
  if (isHorizontal) {
    // Translate leading and trailing to left/right, and flip if rtl
    return [['leading', 'left'].includes(first) ? isRTL ? 'right' : 'left' : isRTL ? 'left' : 'right', last].filter(Boolean).join('-');
  } else {
    // Flip start/end if RTL
    return [first, isRTL ? last === 'end' ? 'start' : last === 'start' ? 'end' : undefined : last].filter(Boolean).join('-');
  }
}
function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
var Popover = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      anchorEl: anchorElProp,
      children,
      fixed = false,
      flip = false,
      id,
      keepMounted = false,
      modifiers = [],
      onChangePlacement,
      open = false,
      placement: placementProp = 'bottom',
      portal = false,
      portalContainer,
      role = 'tooltip',
      style,
      transition = false
    } = _ref,
    popoverProps = _objectWithoutProperties(_ref, _excluded);
  var tooltipRef = React.useRef();
  var mergeTooltipRef = mergeRefs([tooltipRef, ref]);
  var {
    isRTL
  } = useDirection();
  var rtlPlacement = translatePlacement(placementProp, isRTL);
  var [placement, setPlacement] = React.useState(rtlPlacement);
  var [exited, setExited] = React.useState(true);
  var popperJsRef = React.useRef();
  React.useEffect(() => {
    if (popperJsRef.current) {
      popperJsRef.current.forceUpdate();
    }
  });
  var handleOpen = React.useCallback(() => {
    if (!tooltipRef.current || !open || !anchorElProp) {
      return;
    }
    if (popperJsRef.current) {
      popperJsRef.current.destroy();
      popperJsRef.current = undefined;
    }
    var anchorEl = getAnchorEl(anchorElProp);
    if (!anchorEl) {
      return;
    }
    var handlePopperUpdate = newPlacement => {
      setPlacement(newPlacement);
      if (onChangePlacement) onChangePlacement(newPlacement);
    };
    var popperModifiers = [{
      name: 'preventOverflow',
      options: {
        altBoundary: portal
      }
    }, {
      name: 'flip',
      enabled: flip,
      options: {
        altBoundary: portal,
        boundary: 'viewport'
      }
    }, {
      name: 'onUpdate',
      enabled: true,
      phase: 'afterWrite',
      fn: _ref2 => {
        var {
          state
        } = _ref2;
        handlePopperUpdate(state.placement);
      }
    }];
    var popperJs = createPopper(anchorEl, tooltipRef.current, {
      strategy: fixed ? 'fixed' : 'absolute',
      placement: rtlPlacement,
      modifiers: [...popperModifiers, ...modifiers]
    });
    popperJsRef.current = popperJs;
    popperJsRef.current.forceUpdate();
  }, [anchorElProp, fixed, flip, modifiers, open, portal, rtlPlacement]); // eslint-disable-line

  var handleClose = () => {
    if (!popperJsRef.current) {
      return;
    }
    popperJsRef.current.destroy();
    popperJsRef.current = undefined;
  };
  var handleEnter = () => {
    setExited(false);
  };
  var handleExited = () => {
    setExited(true);
    handleClose();
  };
  React.useEffect(() => {
    if (!open && !transition) {
      handleClose();
    }
  }, [open, transition]);
  React.useEffect(() => {
    // componentWillUnmount hook
    return () => {
      handleClose();
    };
  }, []);
  var handleRef = React.useCallback(node => {
    setRef(mergeTooltipRef, node);
    handleOpen();
  }, [handleOpen, mergeTooltipRef]);
  if (!keepMounted && !open && (!transition || exited)) {
    return null;
  }
  var childProps = {
    placement
  };
  if (transition) {
    childProps.transitionProps = {
      in: open,
      onEnter: handleEnter,
      onExited: handleExited
    };
  }
  return ___EmotionJSX(Portal, {
    disablePortal: !portal,
    container: portalContainer
  }, ___EmotionJSX("div", _extends({
    id: id,
    ref: handleRef,
    role: role
  }, popoverProps, {
    style: _objectSpread({
      display: !open && keepMounted && !transition ? 'none' : undefined,
      left: 0,
      position: fixed ? 'fixed' : 'absolute',
      right: 0
    }, style)
  }), typeof children === 'function' ? children(childProps) : children));
});
Popover.propTypes = propTypes;
Popover.displayName = 'Popover';
export default Popover;